import { Group } from 'src/AngularJS/Models/group.model';
import { LoginDTO } from 'src/AngularJS/Dashboard/Modules/tasks.model';
import { ICellRendererComp, ICellRendererParams } from 'ag-grid-community';
import { GridsterItem } from 'angular-gridster2';
import { SafeHtml } from '@angular/platform-browser';
import { DOW, ModuleManagerDateRangeInfo } from '../../shared/models/module-manager/module-manager-settings.models';
import { GroupSelectorDTO } from '../../AngularJS/Reusables/group-selector.models';
import { BaseGroupType } from '../../AngularJS/Models/group-type.model';
import { MetricType } from '../reusables/metric-selector/metric-selector.model';
import { GroupTypeDTO } from '../admin/user/user-admin.model';
import { Chart } from 'chart.js';

export interface WallboardsApiModel {
    Wallboards: Wallboard[];
    Folders: string[];
    Wallboard_Modules: WallboardModule[];
    Domain: string;
    IsBpo: boolean;
}

export class Wallboard {
    WallboardID: number;
    CompanyID: number;
    WallboardName: string;
    WallboardDescription: string;
    CreatorLoginID: number;
    CookieKey: string;
    AllowCookies: boolean;
    AllowInterruptions: boolean;
    ShowInMenu: boolean;
    Active: boolean;
    EnableUrl: boolean;
    RequireLogin: boolean;
    WallboardGroup: string;
    PublicAccessKey: string;
    CreatedDate: Date;
    LastViewed: Date | null;
    FirstSlideType: ModuleType;
    FirstSlideBackgroundID: number;
    FirstSlideKey: string;
    RecipientLogins: LoginDTO[];
    RecipientGroups: any; // Dictionary<string, GroupAccessObj>
    bpoChecked: boolean;
    WallboardAssignments: Group[];
    CreatorGamificationAccess: boolean;

    Slides: WallboardSlide[];
    AllowPublic: boolean;
    isCopy: boolean;
}

export class WallboardGridster extends Wallboard {
    Slides: WallboardSlideGridster[];
}

export class WallboardSlide {
    WallboardSlideID: number;
    CompanyID: number;
    SlideName: string;
    SlideDescription: string;
    WallboardID: number;
    SlideInterval: number;
    SlideOrder: number;
    BackgroundImageID: number;
    TransitionID: number;
    CreatorID: number;
    HasBackdrop: boolean;
    HasTotalsRow: boolean;
    HasPagination: boolean;
    WallboardModuleDetails: WallboardModuleDetails[];
    Enabled: boolean;
    Active: boolean;
    WallboardModuleInfo?: WallboardModule;
    SlideoutSettings?: WallboardSlideSettings;
    FirstSettingOpen?: boolean = false; // Whether the settings have been opened for the first time
    SlideoutSettingsOpen?: boolean = false;
    SlideoutSettingsLoaded?: boolean = false;

    TypeState: TypeStateApiModel; // Used in the launch wallboards component; ideally, wallboards admin would also use something like this
}

export class WallboardSlideGridster extends WallboardSlide {
    WallboardModuleDetails: WallboardModuleDetailsGridster[];
}

export class WallboardModule {
    WallboardModuleID: ModuleType;
    WallboardModuleName: string;
    WallboardModuleDescription: string;
    WallboardModulePath: string;
    WallboardModuleGroup: string;
    FullSize: boolean;
    MaxMetricCount: number;
    SettingEmpSelector: boolean;
    SettingRecSelector: boolean;
    SettingAttSelector: boolean;
    SettingMetricSelector: boolean;
    SettingDateRangeSelector: boolean;
    SettingTimeRangeSelector: boolean;
    DateRangeSelectorHasDOW: boolean;
    GroupSelectorHasMulti: boolean;
    GroupSelectorShowToggle: boolean;
    SettingSlideDuration: boolean;
    SettingShowRanking: boolean;
    SettingUsePagination: boolean;
    SettingShowTotalsRow: boolean;
    SettingBackgroundShadow: boolean;
    SettingShowTitle: boolean;
    SettingColumnSort: boolean;
}

export enum ModuleType { // WallboardModuleID, but ModuleType is more clear as to what this value actually is
    Invalid = -1, // None/invalid
    Custom = 0, // Slide
    Leaderboard = 1, // Slide
    Racing = 2, // Slide
    Scorecard = 3, // Slide --Has not been implemented--
    Summary = 4, // Slide
    Image = 5, // Slide
    Video = 6, // Slide
    Spotlight = 7, // Slide
    Gauge = 8, // Custom module
    Thermometer = 9, // Custom module
    SummaryModule = 10, // Custom module
    Achievement = 11, // Custom module --Has not been implemented--
    Text = 12, // Custom module
    ImageModule = 13, // Custom module
    Top = 14, // Custom module
    // There is no 15
    AgentCounter = 16, // Custom module
    CurrentAgentStates = 17, // Slide
    Kpi = 18, // Custom module
    Games = 19, // Slide
    Challenges = 20, // Slide
    Marketplace = 21, // Slide
    ResponsiveCurrentAgentStates = 22, // Slide
    IntervalChart = 23 // Slide
}

export const SlideModuleTypes = [
    ModuleType.Custom,
    ModuleType.Leaderboard,
    ModuleType.Racing,
    ModuleType.Scorecard,
    ModuleType.Summary,
    ModuleType.IntervalChart,
    ModuleType.Image,
    ModuleType.Video,
    ModuleType.Spotlight,
    ModuleType.CurrentAgentStates,
    ModuleType.ResponsiveCurrentAgentStates,
    ModuleType.Games,
    ModuleType.Challenges,
    ModuleType.Marketplace
] as ModuleType[];

export const CustomModuleTypes = [
    ModuleType.Gauge,
    ModuleType.Thermometer,
    ModuleType.SummaryModule,
    ModuleType.Achievement,
    ModuleType.Text,
    ModuleType.ImageModule,
    ModuleType.Top,
    ModuleType.AgentCounter,
    ModuleType.Kpi,
] as ModuleType[];

export const ModulesUsingSlideout = [
    ModuleType.CurrentAgentStates,
    ModuleType.Summary,
    ModuleType.IntervalChart,
    ModuleType.ResponsiveCurrentAgentStates,
]  as const;

export class WallboardModuleDetails {
    WallboardModuleDetailsID: number;
    CompanyID: number;
    WallboardID: number;
    WallboardSlideID: number;
    PositionTop: number;
    PositionLeft: number;
    Height: number;
    Width: number;
    WallboardModuleID: ModuleType;
    WallboardModuleKey: string;
    Active: boolean;

    State: ModuleListWrapperObj; // Used in the launch wallboards component; ideally, wallboards admin would also use something like this
}

export class WallboardModuleDetailsGridster extends WallboardModuleDetails implements GridsterItem {
    x: number;
    y: number;
    rows: number;
    cols: number;
    minItemRows: number;
    minItemCols: number;

    placeholder: boolean;
}

export interface FullImageDetailsApiModel {
    New_Slide: WallboardSlide;
    New_Image_Id: number;
}

export interface TypeStateApiModel {
    Racing_Setup: RacingSetupTransferObj;
    Image_Settings: ImageSettingsTransferObj;
    Summary_Settings: SummarySettingsTransferObj;
    Interval_Chart_Settings: IntervalChartSettingsTransferObj;
    Video_Settings: VideoSettingsTransferObj;
    Leaderboard_Setup: LeaderboardSetupObj;
    Spotlight_Setup: SpotlightSetupObj;
    Current_Agent_States_Setting: CurrentAgentStatesSettingTransferObj;
    Wallboard_Game_Settings: WallboardGameSetupObj;
    Challenges_Setup: ChallengesSetupObj;
    Marketplace_Setup: MarketplaceSetupObj;
    DisplayName: string;
}

export class ModuleListWrapperObj {
    gaugeObj: GuageSettingsTransferObj;
    thermometerObj: ThermometerSettingsTransferObj;
    textObj: TextSettingsTransferObj;
    topObj: TopSettingsTransferObj;
    imgObj: ImageSettingsTransferObj;
    sumObj: SummarySettingsTransferObj;
    achObj: AchievementSettingsTransferObj;
    counterObj: CounterSettingsTransferObj;
    currentAgentStatesObj: CurrentAgentStatesSettingTransferObj;
    kpiObj: KpiSettingsTransferObj;
}

//#region Setup Objects
export class RacingSetupTransferObj {
    BannerTitle: string;
    Theme: string;
    ShowTop: number;
    Positioning: string;
    StartingLine: number;
    FinishLine: number | null;
    MetricDisplayName: string;
    UseRankSort: boolean;

    InitialSettings: boolean;
}

export class GuageSettingsTransferObj { // The C# object misspells it as "Guage", so I'll keep doing it here
    isOverride: boolean;
    LowerBound: number | null;
    Alert: number | null;
    Value: number | null;
    GaugeTitle: string;
    Theme: string;
    GaugeMetricDisplay: string;

    curLowerBound: number | null;
    curAlert: number | null;
    curValue: number | null;
    poLoaded: boolean;
}

export class ThermometerSettingsTransferObj {
    isOverride: boolean;
    LowerBound: number | null;
    Alert: number | null;
    Value: number | null;
    ThermometerTitle: string;
    Theme: string;
    ThermometerMetricDisplay: string;

    curLowerBound: number | null;
    curAlert: number | null;
    curValue: number | null;
    poLoaded: boolean;
}

export class TextSettingsTransferObj {
    TextValue: string;

    TextValueSanitized: SafeHtml;

    static getTextValueWithoutImage(TextValue: string): string {
        const newValue = TextValue?.replaceAll(/<img[^>]*>/g, "") || ' ';
        return newValue;
    }
}

export class ImageSettingsTransferObj {
    UploadedImageID: number;

    UploadedImageString: string;
    InitialCustomSettings: boolean;
}

export class TopSettingsTransferObj {
    TopTitle: string;
    TopDisplay: string;
    TopMetricDisplay: string;
}

export class KpiSettingsTransferObj {
    KpiTitle: string;
    KpiMetricDisplay: string;
    isOverride: boolean;
    LowerBound: number | null;
    Alert: number | null;
    Value: number | null;
    mCT: number;

    curLowerBound: number | null;
    curAlert: number | null;
    curValue: number | null;
    poLoaded: boolean;
}

export class SummarySettingsTransferObj {
    ShowSummaryTitle: boolean;
    SummaryTitle: string;
    ShowRank: boolean;
    SumMetricDisplays: string[];

    InitialSettings: boolean;
}

export class IntervalChartSettingsTransferObj {
    IntervalChartTitle: string;

    InitialSettings: boolean;
}

export class AchievementSettingsTransferObj {
    AchievementDisplay: string;
}

export class VideoSettingsTransferObj {
    VideoURL: string;
    HasSound: boolean;
    OriginDomain: string;
    ThumbImg: string;

    InitialSettings: boolean;
}

export class LeaderboardSetupObj {
    LeaderboardTitle: string;
    ImageDisplay: string;
    LeaderboardCount: number;

    InitialSettings: boolean;
}

export class SpotlightSetupObj {
    UseLogin: boolean;
    LoginID: number | null;
    LoginName: string;
    ImageDisplay: string;
    ImageID: number | null;
    UploadedImageString: string;
    ShowBio: boolean;
    UseProfileBio: boolean;
    BioString: string;
    ShowHobbies: boolean;
    UseProfileHobbies: boolean;
    HobbyList: string[];
    ShowBadges: boolean;
    ShowBadgeCount: number;

    InitialSettings: boolean;
}

export class CurrentAgentStatesSettingTransferObj {
    SlideTitle: string;

    InitialSettings: boolean;
}

export class WallboardGameSetupObj {
    GameID: number;
    GameType: number;
    DisplayOption: number;

    InitialSettings: boolean;
}

export class ChallengesSetupObj {
    SlideTitle: string;
    TotChallenges: number;
    TotParticipants: number;

    InitialSettings: boolean;
}

export class MarketplaceSetupObj {
    DisplayOption: number;
    SlideTitle: string;
    ProductList: number[];
    ProductTypes: boolean[];

    InitialSettings: boolean;
}

export class CounterSettingsTransferObj {
    CounterTitle: string;
    ShowLabels: boolean;

    chartOptions: Chart<"doughnut", number[], unknown>;
}
//#endregion

export interface SlideControlApiModel {
    Emp_Selector_Html: string;
    Rec_Selector_Html: string;
    Attribute_Html: string;
    Date_Picker_Html: string;
    Time_Range_Html: string;
    Metric_Selector_Html: string;
    Selected_Metric_Info: MetricEventObj[];
}

export class LoginListObj {
    LoginID: number;
    LoginName: string;
}

export class MetricEventObj {
    mID: number;
    mAbr: string;
    mName: string;
    mDir: number;
    DisplayName: string;
    mCT: number;
}

export class ActiveChallengesWsObj {
    RunningChallenges: CurrentChallengeWSObj[];
    UpcomingChallenges: CurrentChallengeWSObj[];
}

export class CurrentChallengeWSObj {
    ChallengeType: number;
    Rank: number;
    TotalParticipants: number;
    Name: string;
    Metrics: string;
    Pot: number;
    Wager: number;
    StartDate: Date;
    StartDate_ForView: string;
    StartDate_NoYear: string;
    EndDate: Date;
    EndDate_ForView: string;
    EndDate_NoYear: string;
    IsMyChallenge: boolean;
    SeededCoins: number;
    SeededPer: boolean;
    RuleID: number;
    ChallengeID: number;
    InWithdrawPeriod: boolean;
    IsRunning: boolean;
    ConversationID: number;
    IntervalAmount: number;
    IntervalType: string;
    lastViewedCount: number;
    CreatorName: string;
    Details; //ChallengeDetailsWSObj
    Rankings; //RankingItemWSObj[]
    NotRankings; //RankingItemWSObj[]
}

export class GamesLandingLoadWsObj {
    GameFolders: any[]; //GamesFolderItemWsObj[]
    GroupTypes: GroupTypeDTO[];
    canRevert: boolean;
}

export class WallboardTextTruncateRenderer implements ICellRendererComp {
    private eGui: HTMLElement;
    
    init(params: ICellRendererParams & { isCxone?: boolean }) {
        this.eGui = document.createElement(`div`);
        this.eGui.className = `d-flex w-100`;
        let value = params.value?.toString() ?? ""; // Added to prevent error when params.value is number type
        const cleanString = value.replaceAll(/</g, '&lt;').replaceAll(/>/g, '&gt;');
        this.eGui.innerHTML = `<div style="${params.isCxone ? 'font-family: SOL Sans, sans-serif; font-size: 12px;' : ''}" class="text-truncate"  title="${cleanString}">${cleanString}</div>`;
    }

    refresh() {
        return false;
    }

    getGui(): HTMLElement {
        return this.eGui;
    }
}

export class ProductCategory {
    CategoryName: string;
    SubCategories: string[];
}

export class WallboardModuleSettings {
    ModuleKey: string;
    DateRange: string;
}

export class WallboardSlideSettings {
    ModuleKey: string;
    DateRange: ModuleManagerDateRangeInfo;
    AvailableDateRanges: ModuleManagerDateRangeInfo[];
    DaysOfWeek: DOW;
    TimeSelector: TimeSelectorSettings;
    EmpSelectorDTO: GroupSelectorDTO;
    RecSelectorDTO: GroupSelectorDTO;
    SelectedMetrics: MetricInfo[];
    SelectedLevelName: string;
    SelectedToggle: BaseGroupType; 
    DataAttributesModel: any;
    GroupedCategoryID: number;
    GeneralWallboardSettings: GeneralWallboardSettings;
    ColumnSortSettings: WallboardColumnSortSettings;
}

export class MetricInfo {
    Id: number;
    Abbreviation: string;
    Name: string;
    ConversionType: number;
    Direction: number;
    MetricType: MetricType;
}

export class TimeSelectorSettings {
    TimeSelectorEnabled?: boolean;
    InvertTimeSelector?: boolean;
    MinTime?: number;
    MaxTime?: number;
}

export class GeneralWallboardSettings {
    SlideName: string;
    SlideDuration: number = 15; //min is 15 sec
    UseBackdrop: boolean;
    UsePagination: boolean;
    UseTotalsRow: boolean;
    ShowRanking: boolean;
    ShowTitle: boolean;
}

export class WallboardColumnSortSettings {
    ColumnToSort: number;
    SortAscending: boolean;
}
